/**
 * Wires the Jobs and Nav sections (W4b card C3) to the live game: the career (M2 runtime), the Reach (station
 * directory, route, travel state, commands), the story objective and the maps feature. Everything is read lazily
 * when a section renders (4 Hz while open), so the sections stay registered across career starts and show their
 * empty state outside a career in the Reach.
 */
import type { GameContext } from '../../../../app/feature'
import type { Career } from '../../../../career/types'
import { visibleBoard } from '../../../../contracts/board'
import { stationIndexOf } from '../../../../data/m2'
import type { Vec3T } from '../../../../galaxy/layout/types'
import type { ReachCommandResult } from '../../../../reach/types'
import { objectiveLine } from '../../../../features/storyHud/model'
import mapsFeature from '../../../../features/maps/feature'
import { Pos64 } from '../../../../world/components'
import { UI_CONFIG } from '../../../config'
import { formatCr } from '../../../format'
import { requirementText } from '../../../models/contracts'
import { systemNameIn } from '../../../strategic/names'
import { loadDashboardConfig } from '../../config'
import { createReachStationDirectory, setCourseTo, stationAge, type SeenSource, type StationDirectory } from '../../data'
import { closeDashboard, openDashboard, registerDashSection } from '../../registry'
import type { CourseTarget } from '../jobs/model'
import { createJobsSections } from '../jobs/sections'
import { createNavSections } from './sections'

const NO_REACH: ReachCommandResult = { ok: false, error: 'invalid', message: 'Not in the Reach' }

/** Registers Jobs › Active, Story, Boards and Nav › Route, Nearby, Map; returns the unregister. */
export function installJobsNavSections(ctx: GameContext): () => void {
  const cfg = loadDashboardConfig()
  let dirFor: { reach: object; dir: StationDirectory } | null = null
  const pos: [number, number, number] = [0, 0, 0]

  const career = (): Career | null => {
    const r = ctx.services.maybe('m2')
    return r === undefined ? null : r.career()
  }
  const gameTime = (): number => ctx.services.maybe('m2')?.space.mirror.gameTime ?? 0
  const hereSystemId = (): string | null => ctx.reach?.session.layout?.systemId ?? null

  const dir = (): StationDirectory | null => {
    const reach = ctx.reach
    if (reach === null) return null
    if (dirFor?.reach !== reach) {
      dirFor = {
        reach,
        dir: createReachStationDirectory({
          galaxy: reach.galaxy,
          state: reach.state,
          layoutOf: (id) => reach.layoutOf(id),
          hereSystemId,
          herePosition: (): Vec3T | null => {
            const p = ctx.sim.playerEid
            if (p < 0 || hereSystemId() === null) return null
            pos[0] = Pos64.x[p]
            pos[1] = Pos64.y[p]
            pos[2] = Pos64.z[p]
            return pos
          },
        }),
      }
    }
    return dirFor.dir
  }

  const seen = (c: Career): SeenSource => {
    const here = hereSystemId()
    return { priceLog: c.priceLog, covered: new Set(here === null ? [] : [here]) }
  }

  const course = (target: CourseTarget): ReachCommandResult => {
    const reach = ctx.reach
    const d = dir()
    if (reach === null || d === null) return NO_REACH
    if (target.kind === 'station') return setCourseTo(reach, d, target.id)
    return reach.command({ kind: 'plotRoute', toSystemId: target.id, avoidHostile: false })
  }

  const systemName = (id: string): string => systemNameIn(ctx.reach?.galaxy, id) ?? id

  const jobs = createJobsSections({
    career,
    gameTime,
    dir,
    activeDeps: () => {
      const data = ctx.services.maybe('m2')?.data
      return {
        typeLabel: (t) => UI_CONFIG.contractTypes[t],
        requirement: (c) => (data === undefined ? '' : requirementText(data, c.type, c.station, c.params)),
        stationName: (id) => {
          if (data === undefined) return null
          const i = stationIndexOf(data, id)
          return i < 0 ? null : data.stations.stations[i].name
        },
      }
    },
    boardsDeps: () => {
      const c = career()
      const data = ctx.services.maybe('m2')?.data
      if (c === null || data === undefined) return null
      const now = gameTime()
      const src = seen(c)
      return {
        age: (st) => stationAge(cfg.age, src, st, 'board', now, null),
        offers: (stationId) =>
          stationIndexOf(data, stationId) < 0
            ? []
            : visibleBoard(data, c, stationId).map((o) => ({
                id: o.id,
                typeLabel: UI_CONFIG.contractTypes[o.type],
                requirement: requirementText(data, o.type, o.station, o.params),
                reward: formatCr(o.reward),
                premium: o.premium,
              })),
      }
    },
    objective: () => {
      const c = career()
      return c === null ? '' : objectiveLine(c)
    },
    course,
    targetName: (t) => (t.kind === 'system' ? systemName(t.id) : (dir()?.stations.find((s) => s.id === t.id)?.name ?? t.id)),
  })

  const nav = createNavSections({
    route: () => {
      const reach = ctx.reach
      if (reach === null) return null
      return {
        route: reach.state.route,
        hereSystemId: hereSystemId(),
        systemName,
        mode: reach.travel.mode,
        massLock: reach.travel.massLock,
        etaS: reach.travel.etaS,
      }
    },
    dir,
    clearRoute: () => ctx.reach?.command({ kind: 'clearRoute' }) ?? NO_REACH,
    setCourse: (id) => course({ kind: 'station', id }),
    openMap: (kind) => {
      const here = hereSystemId()
      closeDashboard(false)
      mapsFeature.open(ctx, kind === 'galaxy' && here !== null ? here : undefined)
      if (mapsFeature.phase === 'closed') {
        openDashboard({ tab: 'nav', section: 'nav.map' })
        return
      }
      mapsFeature.returnTo(() => openDashboard({ tab: 'nav', section: 'nav.map' }))
    },
  })

  const offs = [...jobs, ...nav].map((s) => registerDashSection(s))
  return (): void => {
    for (const off of offs.splice(0)) off()
  }
}
